<script setup lang="ts">
import { computed } from 'vue'
import LibraryCard from './LibraryCard.vue'
import RemoveResourceButton from './RemoveResourceButton.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import DynamicHeading from '@/components/DynamicHeading.vue'
import { toolkitUrls, urlHelpers } from '@/util/links'
import { countWikitextParts } from '@/components/editor/wiki_tiptap_helpers'
import { PropType } from 'vue'
import { LibraryContentObject, LibraryPresentLevelStudentSummary } from '@/library/library_types'
import CardContributor from './CardContributor.vue'
import { useContributorVisibility } from './composables/useContributorVisibility'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { storeToRefs } from 'pinia'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'

const analytics = getAnalyticsTracker()
const { getEventData } = useLibraryFolderAnalytics()

const props = defineProps({
  savedSubjectSummary: {
    type: Object as PropType<LibraryContentObject>,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  clickable: {
    type: Boolean,
    default: true
  },
  headingLevel: {
    type: Number,
    default: 3
  },
  shared: {
    type: Boolean,
    default: false
  }
})

const resourceType = CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY
const savedPresentLevelsUrl = computed(() => {
  let url = props.shared
    ? toolkitUrls.librarySharedPresentLevelsSummary(props.savedSubjectSummary.content_uuid)
    : toolkitUrls.libraryPresentLevelsSubjectSummaryPage(props.savedSubjectSummary.content_uuid)
  if (betaVisibility.value) {
    url = urlHelpers.betaModeUrl(url)
  }
  return url
})
const resourceCount = computed(() => {
  return countWikitextParts((props.savedSubjectSummary.content_body as LibraryPresentLevelStudentSummary).statement_text)
})
const summary = computed(() => props.savedSubjectSummary.content_body as LibraryPresentLevelStudentSummary)

const { contributorIsVisible } = useContributorVisibility(props.savedSubjectSummary.content_uuid)
const { betaVisibility } = storeToRefs(useVisibilityStore())

const handleRemoveEvent = () => {
  analytics.sendEvent(
    EVENT_NAMES.PRESENTLEVELS.LIBRARY_SUMMARY.REMOVED,
    {
      ...getEventData.value,
      plw_is_library: true,
      plw_statement_subject: summary.value.subject_label,
      plw_session_enrolled_grade: summary.value.grade_label
    }
  )
}

</script>
<template>
  <div
    class="relative [&_.remove-resource-button]:hover:opacity-100 [&_.remove-resource-button]:focus:opacity-100 [&_.card-title]:hover:text-gray-1000 [&_.card-title]:focus:text-gray-1000"
  >
  <LibraryCard
    :content-uuid="savedSubjectSummary.content_uuid"
    :url="savedPresentLevelsUrl"
    :show-folder-name="savedSubjectSummary.show_folder_name"
    :folder-name="savedSubjectSummary.collection_body?.name"
    :updated-time="summary.updated_at"
    v-bind:clickable="clickable"
    >
      <DynamicHeading
        :headingLevel="props.headingLevel"
        :class="`header-sm ${contributorIsVisible ? 'pb-0' : 'pb-3'} text-xl text-primary-500 card-title`"
        data-cy="present-levels-title"
      >
        {{ summary.subject_label }}
      </DynamicHeading>
      <CardContributor
        v-if="!shared"
        :contentId="savedSubjectSummary.content_uuid"
      />
      <div class="flex items-center justify-between">
        <div
          class="min-h-11 overflow-hidden text-ellipsis text-md [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box] fs-exclude"
        >
          <p>{{ summary.student_name }}</p>
          <p>{{ summary.grade_label }}</p>
        </div>
      </div>
      <template #footer-right>
        <span class="flex items-center text-sm font-bold">
          <span class="material-icons-outlined text-lg">file_download</span>
          {{ resourceCount }}
        </span>
      </template>
    </LibraryCard>
    <RemoveResourceButton
      v-if="removable"
      :resource="savedSubjectSummary.content_uuid"
      :collection="savedSubjectSummary.collection_uuid"
      :type="resourceType"
      :ariaLabel="`Remove ${summary.subject_label} from collection`"
      :onRemoveCallback="handleRemoveEvent"
    />
  </div>
</template>
